import { Duration } from 'luxon';

export const SECONDS_PER_MINUTE = 60;
export const MINUTES_PER_HOUR = 60;
export const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;

/**
 * Formats a duration given in seconds as hours and minutes, e.g. "2 h 30 min".
 * Durations below one hour only show the minutes. Remaining seconds are dropped.
 */
export function formatHoursMinutes(seconds?: number): string {
  const duration = Duration.fromObject({ seconds }).rescale().set({
    seconds: 0,
    milliseconds: 0,
  });

  // use SI units 'h' and 'min' for any later localization
  return seconds && seconds >= SECONDS_PER_HOUR
    ? duration.toFormat("h 'h' m 'min'")
    : duration.toFormat("m 'min'");
}

/**
 * Splits a duration in seconds into whole hours and whole minutes. Remaining seconds are dropped.
 */
export function splitHoursMinutes(seconds?: number): { hours?: number; minutes?: number } {
  if (seconds === undefined || !Number.isFinite(seconds)) {
    return {};
  }
  const totalMinutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  return {
    hours: Math.floor(totalMinutes / MINUTES_PER_HOUR),
    minutes: totalMinutes % MINUTES_PER_HOUR,
  };
}

/**
 * Combines hours and minutes into seconds. A missing part counts as zero,
 * both parts missing yield `undefined`.
 */
export function toSeconds(hours?: number, minutes?: number): number | undefined {
  if (hours === undefined && minutes === undefined) {
    return undefined;
  }
  return (hours ?? 0) * SECONDS_PER_HOUR + (minutes ?? 0) * SECONDS_PER_MINUTE;
}
